import { clearStorage } from '@getjust/commons';
import { UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';

import type { Cart } from '@getjust/gateway';

import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdAtom, useROFormMessageAtom } from '$src/hooks/state';
import { gateway, hasXCountryGatewayHeader } from '$src/http';
import { clearCachedCart } from '$src/utils/cartCache';

export function useCart(): UseQueryResult<Cart> {
  const baseUrl = useBaseApiUrl();
  const queryClient = useQueryClient();
  const { cartId, onCartIdChange } = useCartIdAtom();
  const message = useROFormMessageAtom();
  const query = useQuery<Cart>({
    queryKey: [CART_QUERY_KEY, cartId],
    queryFn: async () => {
      const response = await gateway.get<Cart>(`${baseUrl}`);
      if (response?.data?.taxReady === false || response?.data?.shipping?.ready === false) {
        queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
      }
      return response.data;
    },
    enabled: Boolean(baseUrl) && Boolean(hasXCountryGatewayHeader()) && Boolean(cartId),
  });

  // Cart-deleted-server-side recovery. A 404 means our cached cart id no
  // longer resolves — drop both localStorage entries and reset the atom
  // so the cold path (useRedirectSetData → prepareCart) takes over on
  // next URL data load, instead of looping on a 404.
  useEffect(() => {
    if (!query.isError) return;
    const err = query.error as unknown;
    if (!(err instanceof AxiosError)) return;
    if (err.response?.status !== 404) return;
    const shopId = message?.shopId;
    if (shopId) {
      clearStorage('localStorage', `just_session_id_${shopId}`);
      clearCachedCart(shopId);
    }
    onCartIdChange(undefined);
  }, [query.isError, query.error, message?.shopId, onCartIdChange]);

  return query;
}
